// Lets a mouse drag a horizontally scrolling strip. Touch and trackpads keep native scrolling.
// After a real drag, the click that follows is swallowed so links inside don't open by accident.
// A glide continues the drag's momentum unless the visitor prefers reduced motion; `data-glide` (phones) and
// `data-glide-desktop` (0-100) set how loose it is. Phones with a glide above 0 handle sideways swipes here too.
// With the `loop` attribute the slides are cloned before and after the originals and the scroll
// position is shifted by exactly one set when it nears either end, so the strip never runs out.
// With the `autoscroll` attribute the strip moves by itself: `data-mode="auto"` drifts at `data-speed` pixels per
// second, `data-mode="scroll"` moves only while the page scrolls (`data-speed` 100 = as far as the page moved), right to left
// (`data-direction="rtl"`, the default) or left to right (`ltr`). It pauses while a mouse hovers over it,
// while it is dragged, touched or keyboard-focused, while it is off screen, and for reduced motion.
// `data-hover-pause="false"` keeps it moving under the mouse.
// In scroll mode the strip keeps sliding after the page stops and eases out, as far as the Glide setting allows.
// `no-drag` turns off mouse dragging (pair it with overflow: hidden so touch and trackpads cannot scroll it either).
// Without `loop` it reverses at either end.
// Clones are hidden from assistive tech and inert, so keyboard and screen reader users meet each slide once.
class SDragScroll extends HTMLElement {
  #controller = null;
  #startX = 0;
  #startScroll = 0;
  #lastX = 0;
  #lastTime = 0;
  #velocity = 0;
  #moved = false;
  #pointerId = null;
  #glideFrame = 0;
  #setWidth = 0;
  #wrapTimer = 0;
  #resizeObserver = null;
  #autoFrame = 0;
  #autoLast = 0;
  #autoPos = 0;
  #autoDir = -1;
  #hovered = false;
  #touching = false;
  #focused = false;
  #visible = true;
  #touchTimer = 0;
  #intersection = null;
  #lastY = 0;
  #scrollVel = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('pointerdown', (event) => this.#onDown(event), { signal });
    this.addEventListener('pointermove', (event) => this.#onMove(event), { signal });
    this.addEventListener('pointerup', (event) => this.#onUp(event), { signal });
    this.addEventListener('pointercancel', (event) => this.#onUp(event), { signal });
    this.addEventListener(
      'click',
      (event) => {
        if (!this.#moved) return;
        event.preventDefault();
        event.stopPropagation();
        this.#moved = false;
      },
      { capture: true, signal },
    );
    this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    if (this.hasAttribute('loop')) this.#setupLoop(signal);
    if (this.hasAttribute('autoscroll')) this.#setupAutoscroll(signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#glideFrame);
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#wrapTimer);
    cancelAnimationFrame(this.#autoFrame);
    clearTimeout(this.#touchTimer);
    this.#intersection?.disconnect();
  }

  #setupLoop(signal) {
    const list = this.querySelector(':scope > ul');
    if (!list || list.dataset.looped !== undefined) return;
    const originals = [...list.children];
    if (originals.length < 2) return;
    list.dataset.looped = '';

    const makeClones = () =>
      originals.map((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        return clone;
      });
    list.prepend(...makeClones());
    list.append(...makeClones());

    const measure = () => {
      const previousSet = this.#setWidth;
      this.#setWidth = originals[0].offsetLeft - list.children[0].offsetLeft;
      if (!previousSet) {
        this.scrollLeft = this.#setWidth;
      } else if (previousSet !== this.#setWidth) {
        this.scrollLeft = (this.scrollLeft / previousSet) * this.#setWidth;
      }
    };
    measure();
    this.#resizeObserver = new ResizeObserver(measure);
    this.#resizeObserver.observe(list);

    this.addEventListener(
      'scroll',
      () => {
        // Near an edge: wrap at once so scrolling never stops. Otherwise recentre once the strip
        // settles, so touch momentum is never interrupted mid-swipe.
        const max = this.scrollWidth - this.clientWidth;
        if (this.scrollLeft < 2 || this.scrollLeft > max - 2) this.#wrap();
        clearTimeout(this.#wrapTimer);
        this.#wrapTimer = setTimeout(() => this.#wrap(), 160);
      },
      { passive: true, signal },
    );
  }

  #setupAutoscroll(signal) {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches) return;
    const desktop = matchMedia('(min-width: 768px)');
    desktop.addEventListener('change', () => { this.#autoDir = 1; }, { signal });
    this.#autoDir = 1; // bounce multiplier for strips without loop

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse' && this.dataset.hoverPause !== 'false') this.#hovered = true;
    }, { signal });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.#hovered = false;
    }, { signal });
    this.addEventListener('touchstart', () => {
      if (this.hasAttribute('no-drag')) return;
      this.#touching = true;
      clearTimeout(this.#touchTimer);
    }, { passive: true, signal });
    this.addEventListener('touchend', () => {
      clearTimeout(this.#touchTimer);
      this.#touchTimer = setTimeout(() => { this.#touching = false; }, 2500);
    }, { passive: true, signal });
    this.addEventListener('focusin', () => { this.#focused = this.matches(':has(:focus-visible)'); }, { signal });
    this.addEventListener('focusout', () => { this.#focused = false; }, { signal });

    this.#intersection = new IntersectionObserver((entries) => {
      this.#visible = entries.some((entry) => entry.isIntersecting);
    });
    this.#intersection.observe(this);

    this.#autoPos = this.scrollLeft;
    this.#lastY = window.scrollY;
    const tick = (time) => {
      const elapsed = this.#autoLast ? Math.min(time - this.#autoLast, 64) : 0;
      this.#autoLast = time;
      const paused = this.#hovered || this.#touching || this.#focused || !this.#visible
        || this.#pointerId !== null || this.dataset.dragging !== undefined;
      // Pick up any change made by a drag, swipe or loop wrap since the last frame.
      if (Math.abs(this.scrollLeft - this.#autoPos) > 1.5) this.#autoPos = this.scrollLeft;
      // Phones and desktop each have their own mode, speed and direction. Right to left (default) means
      // content travels left, so the scroll position grows.
      const wide = desktop.matches;
      const mode = (wide ? this.dataset.modeDesktop : this.dataset.mode) || 'off';
      const speed = Number(wide ? this.dataset.speedDesktop : this.dataset.speed) || 20;
      const dir = ((wide ? this.dataset.directionDesktop : this.dataset.direction) === 'ltr' ? -1 : 1) * this.#autoDir;
      if (paused) this.#scrollVel = 0;
      if (!paused && mode !== 'off' && elapsed > 0) {
        const byScroll = mode === 'scroll';
        let step = speed * elapsed / 1000;
        if (byScroll) {
          // Follow the page while it scrolls; once it stops, keep sliding and ease out using the Glide setting.
          const delta = (window.scrollY - this.#lastY) * (speed / 100);
          if (delta !== 0) {
            this.#scrollVel = delta;
          } else {
            const glide = this.#glideAmount();
            this.#scrollVel = glide > 0 ? this.#scrollVel * (0.9 + glide * 0.00095) : 0;
            if (Math.abs(this.#scrollVel) < 0.05) this.#scrollVel = 0;
          }
          step = this.#scrollVel;
        }
        this.#autoPos += step * dir;
        if (!this.hasAttribute('loop')) {
          const max = this.scrollWidth - this.clientWidth;
          // On their own: bounce at the ends. With page scroll: just stop at the end.
          if (this.#autoPos >= max) { this.#autoPos = max; if (!byScroll) this.#autoDir *= -1; }
          else if (this.#autoPos <= 0) { this.#autoPos = 0; if (!byScroll) this.#autoDir *= -1; }
        }
        if (step !== 0) this.scrollLeft = this.#autoPos;
      }
      this.#lastY = window.scrollY;
      this.#autoFrame = requestAnimationFrame(tick);
    };
    this.#autoFrame = requestAnimationFrame(tick);
  }

  #wrap() {
    const set = this.#setWidth;
    if (!set) return;
    let shift = 0;
    if (this.scrollLeft < set * 0.5) shift = set;
    else if (this.scrollLeft > set * 1.5) shift = -set;
    if (!shift) return;
    this.scrollLeft += shift;
    this.#startScroll += shift;
  }

  #onDown(event) {
    const isMouse = event.pointerType === 'mouse' && event.button === 0;
    // Phones with a custom Glide: the strip handles sideways swipes itself (vertical page scrolling stays native).
    const isTouchGlide = event.pointerType === 'touch' && !this.#isWide() && this.#glideAmount() > 0;
    if ((!isMouse && !isTouchGlide) || this.hasAttribute('no-drag')) return;
    cancelAnimationFrame(this.#glideFrame);
    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#lastX = event.clientX;
    this.#lastTime = event.timeStamp;
    this.#startScroll = this.scrollLeft;
    this.#velocity = 0;
    this.#moved = false;
  }

  #onMove(event) {
    if (event.pointerId !== this.#pointerId) return;
    const distance = event.clientX - this.#startX;
    if (!this.#moved && Math.abs(distance) < 5) return;
    if (!this.#moved) {
      this.#moved = true;
      this.dataset.dragging = '';
      this.setPointerCapture(event.pointerId);
    }
    const elapsed = Math.max(event.timeStamp - this.#lastTime, 1);
    this.#velocity = (event.clientX - this.#lastX) / elapsed;
    this.#lastX = event.clientX;
    this.#lastTime = event.timeStamp;
    this.scrollLeft = this.#startScroll - distance;
  }

  #onUp(event) {
    if (event.pointerId !== this.#pointerId) return;
    this.#pointerId = null;
    delete this.dataset.dragging;
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    if (this.#moved && !matchMedia('(prefers-reduced-motion: reduce)').matches) this.#glide();
  }

  #isWide() {
    return matchMedia('(min-width: 768px)').matches;
  }

  // Glide 0-100 from data-glide (phones) or data-glide-desktop: how far and how long the strip keeps
  // sliding after a drag or swipe. Higher is looser.
  #glideAmount() {
    const value = this.#isWide() ? this.dataset.glideDesktop : this.dataset.glide;
    const amount = Number(value);
    if (value === undefined || Number.isNaN(amount)) return this.#isWide() ? 40 : 0;
    return Math.min(Math.max(amount, 0), 100);
  }

  #glide() {
    const amount = this.#glideAmount();
    const friction = 0.9 + amount * 0.00095;
    let velocity = this.#velocity * (12 + amount * 0.12);
    const step = () => {
      if (Math.abs(velocity) < 0.5) return;
      this.scrollLeft -= velocity;
      velocity *= friction;
      this.#glideFrame = requestAnimationFrame(step);
    };
    this.#glideFrame = requestAnimationFrame(step);
  }
}

if (!customElements.get('s-drag-scroll')) customElements.define('s-drag-scroll', SDragScroll);
